/**
 * Shared document validation utilities
 * Used by DocumentsPage and DocumentsHistory to avoid duplication
 */

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_UPLOAD_MB = 10;

export const ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
]);

export const HEIC_EXTENSIONS = ['.heic', '.heif'];

export const CONTEXT_DOMAIN_HINTS: Record<string, string> = {
    resume: 'job_hunt',
    bill: 'finance',
    event: 'personal',
    official: 'finance', // Government/legal often finance-related
};

export const isHeicFile = (file: File): boolean => {
    const type = (file.type || '').toLowerCase();
    if (type === 'image/heic' || type === 'image/heif') return true;
    const name = (file.name || '').toLowerCase();
    return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
};

export const getHintDomain = (intent?: string | null): string | undefined => {
    if (!intent) return undefined;
    const key = intent.trim().toLowerCase();
    return CONTEXT_DOMAIN_HINTS[key];
};

export const getApiErrorMessage = (error: any): string => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        const nested = detail.message || detail.error || detail.detail;
        if (typeof nested === 'string') return nested;
    }
    return error?.message || error?.error_description || 'Request failed.';
};

export interface FileValidationResult {
    valid: boolean;
    error?: string;
    friendlyError?: string; // User-friendly message
}

export const validateFile = (file: File): FileValidationResult => {
    if (file.size > MAX_UPLOAD_BYTES) {
        return {
            valid: false,
            error: 'File too large',
            friendlyError: `This file is too large (max ${MAX_UPLOAD_MB}MB). Try compressing or splitting it.`,
        };
    }

    if (isHeicFile(file)) {
        return {
            valid: false,
            error: 'HEIC not supported',
            friendlyError: 'iPhone photos in HEIC format aren\'t supported yet. Please convert to JPG or PNG first.',
        };
    }

    if (!ALLOWED_MIME_TYPES.has(file.type)) {
        return {
            valid: false,
            error: 'Unsupported file type',
            friendlyError: 'We support PDF, JPG, PNG, Word, and TXT files. Try converting this file first.',
        };
    }

    return { valid: true };
};

/**
 * Transform technical error messages into user-friendly ones
 */
export const humanizeError = (detail: string): string => {
    if (detail.includes('Scanned or image-based PDF')) {
        return 'This PDF is a scanned image. Could you snap a photo directly instead? Tap "Snap Photo" to try.';
    }
    if (detail.includes('API Key missing') || detail.includes('OPENAI_API_KEY')) {
        return 'We couldn\'t analyze this document right now. Our team has been notified.';
    }
    if (detail.includes('rate limit')) {
        return 'You\'re uploading quickly! Please wait a moment and try again.';
    }
    if (detail.includes('Empty document') || detail.includes('EMPTY_CONTENT')) {
        return 'This document appears to be empty. Try uploading a different version.';
    }
    if (detail.includes('parse') || detail.includes('Failed to parse')) {
        return 'We had trouble reading this document. Try re-uploading or using a clearer image.';
    }
    return detail;
};
